/**
 * Kiosk voice-note recorder.
 *
 * Flow:
 *   /room/:pin/voice  →  PIN validated → record screen → submit → ack.
 *
 * Uses MediaRecorder (universal browser support on HTTPS) to capture a short
 * audio clip, encodes it as a base64 data URL, then POSTs to /api/voice-notes.
 *
 * The kiosk lets the visitor optionally pick a department (e.g. "IT" vs
 * "Hospitality"). If they don't pick one, the server falls back to the
 * per-location default configured by the admin.
 */
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Microphone, Stop, ArrowLeft, PaperPlaneTilt, CheckCircle, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const MAX_SECONDS = 60;

export default function RoomVoice() {
    const { pin } = useParams();
    const navigate = useNavigate();
    const [room, setRoom] = useState(null);
    const [depts, setDepts] = useState([]);
    const [chosenDept, setChosenDept] = useState("__default__");
    const [recording, setRecording] = useState(false);
    const [secs, setSecs] = useState(0);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const mediaRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.post("/rooms/access", { pin });
                setRoom(data);
                // Fetch this location's departments for the picker.
                const d = await api.get(`/departments?location_id=${data.location_id || ""}`).catch(() => ({ data: [] }));
                setDepts(d.data || []);
            } catch {
                toast.error("Invalid PIN");
                navigate("/");
            }
        })();
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            // Make sure we release the mic & stop the tracks when leaving the page.
            try { mediaRef.current?.getTracks?.().forEach((t) => t.stop()); } catch { /* noop */ }
        };
    }, [pin, navigate]);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRef.current = stream;
            // Pick the most-compatible mime type the browser supports. Safari
            // only does audio/mp4; Chrome/Firefox prefer audio/webm.
            const mimeCandidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
            const mime = mimeCandidates.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
            const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
            chunksRef.current = [];
            rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            rec.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
                setAudioBlob(blob);
                setAudioUrl(URL.createObjectURL(blob));
                stream.getTracks().forEach((t) => t.stop());
                mediaRef.current = null;
            };
            recorderRef.current = rec;
            rec.start();
            setRecording(true);
            setSecs(0);
            timerRef.current = setInterval(() => {
                setSecs((s) => {
                    if (s + 1 >= MAX_SECONDS) { stopRecording(); return s; }
                    return s + 1;
                });
            }, 1000);
        } catch (err) {
            toast.error("Microphone permission was denied. Please allow it and try again.");
        }
    };

    const stopRecording = () => {
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
            recorderRef.current.stop();
        }
        setRecording(false);
    };

    const reset = () => {
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        setAudioBlob(null);
        setAudioUrl(null);
        setSecs(0);
    };

    const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(blob);
    });

    const submit = async () => {
        if (!audioBlob) return;
        setSubmitting(true);
        try {
            const dataUrl = await blobToDataUrl(audioBlob);
            await api.post("/voice-notes", {
                room_id: room.id,
                pin,
                audio_data_url: dataUrl,
                duration_seconds: secs,
                department_id: chosenDept === "__default__" ? null : chosenDept,
            });
            setDone(true);
            toast.success("Voice note sent");
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to send");
        } finally {
            setSubmitting(false);
        }
    };

    if (!room) {
        return <div className="min-h-screen grid place-items-center text-gray-400">Loading…</div>;
    }

    return (
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-blue-50 dark:from-[#0B0D10] dark:to-[#0F1419]">
            <header className="border-b border-gray-200 dark:border-gray-800 bg-white/70 dark:bg-[#0B0D10]/70 backdrop-blur-sm">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Button variant="ghost" onClick={() => navigate(`/room/${pin}`)} data-testid="voice-back-btn">
                        <ArrowLeft size={18} className="mr-1" /> Back
                    </Button>
                    <div className="text-center">
                        <div className="label-eyebrow text-gray-500">{room.name}</div>
                        <div className="font-display font-semibold">Voice Note</div>
                    </div>
                    <div style={{ width: 80 }} />
                </div>
            </header>

            <main className="flex-1 max-w-2xl w-full mx-auto p-6">
                {done ? (
                    <Card className="p-12 text-center" data-testid="voice-done-card">
                        <CheckCircle size={56} weight="fill" className="text-emerald-500 mx-auto" />
                        <div className="font-display text-2xl font-semibold text-gray-900 mt-4">Voice note sent</div>
                        <div className="text-gray-500 mt-1">The team will get back to you shortly.</div>
                        <div className="flex gap-2 justify-center mt-6">
                            <Button variant="outline" onClick={() => { reset(); setDone(false); }} data-testid="voice-record-another">Record another</Button>
                            <Button onClick={() => navigate(`/room/${pin}`)} className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="voice-back-to-room">Back to room</Button>
                        </div>
                    </Card>
                ) : (
                    <Card className="p-6 sm:p-8 space-y-6">
                        <div>
                            <div className="label-eyebrow text-gray-500">Step 1 · Route to</div>
                            <Select value={chosenDept} onValueChange={setChosenDept}>
                                <SelectTrigger className="mt-1" data-testid="voice-dept-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__default__">Default (per room admin)</SelectItem>
                                    {depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <div className="text-[11px] text-gray-500 mt-1">If you don&apos;t pick one, the message goes to the department the admin set for this location.</div>
                        </div>

                        <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6">
                            <div className="text-center">
                                <div className={`mx-auto w-24 h-24 rounded-full grid place-items-center transition-colors ${recording ? "bg-red-50 animate-pulse" : "bg-gray-50"}`}>
                                    <Microphone size={40} weight={recording ? "fill" : "duotone"} className={recording ? "text-red-500" : "text-gray-500"} />
                                </div>
                                <div className="mt-3 font-mono text-2xl tabular-nums" data-testid="voice-timer">
                                    {String(Math.floor(secs / 60)).padStart(2, "0")}:{String(secs % 60).padStart(2, "0")}
                                </div>
                                <div className="text-xs text-gray-500">Up to {MAX_SECONDS}s</div>
                            </div>

                            <div className="flex justify-center gap-2 mt-5">
                                {!recording && !audioBlob && (
                                    <Button onClick={startRecording} className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="voice-record-btn">
                                        <Microphone size={18} className="mr-1.5" /> Start recording
                                    </Button>
                                )}
                                {recording && (
                                    <Button onClick={stopRecording} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" data-testid="voice-stop-btn">
                                        <Stop size={18} weight="fill" className="mr-1.5" /> Stop
                                    </Button>
                                )}
                                {audioBlob && !recording && (
                                    <>
                                        <Button variant="outline" onClick={reset} data-testid="voice-discard-btn">
                                            <Trash size={16} className="mr-1.5" /> Discard
                                        </Button>
                                        <Button onClick={submit} disabled={submitting} className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="voice-submit-btn">
                                            <PaperPlaneTilt size={16} className="mr-1.5" /> {submitting ? "Sending…" : "Send"}
                                        </Button>
                                    </>
                                )}
                            </div>

                            {audioUrl && !recording && (
                                <audio src={audioUrl} controls className="w-full mt-4" data-testid="voice-playback" />
                            )}
                        </div>
                    </Card>
                )}
            </main>
        </div>
    );
}
